import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import { showSnackbar } from '../../../Reducer/SnackbarReducer';

const maxFileSize = 50 * 1024 * 1024; // 50MB
const extensionMap = {
  docx: {
    exts: ['docx'],
    mime: 'application/docx',
    message: '워드(docx) 파일만 업로드 가능합니다.',
  },

  xls: {
    exts: ['xls', 'xlsx', 'cell'],
    mime: 'application/xls',
    message: '엑셀 파일만 업로드 가능합니다.',
  },
  pdf: {
    exts: ['pdf'],
    mime: 'application/pdf',
    message: 'PDF 파일만 업로드 가능합니다.',
  },
  hwp: {
    exts: ['hwp', 'hwpx'],
    mime: 'application/hwp',
    message: '한글 파일만 업로드 가능합니다.',
  },
  img: {
    exts: ['jpeg', 'jpg', 'png'],
    mime: 'image/png',
    message: 'JPEG 또는 PNG 파일만 업로드 가능합니다.',
  },
  video: {
    exts: ['mp4'],
    mime: 'video/mp4', // 변경 필요
    message: '비디오 파일만 업로드 가능합니다.',
  },
  ppt: {
    exts: ['ppt', 'pptx'],
    mime: 'application/pptx',
    message: 'PPT 파일은 업로드할 수 없습니다.',
    blocked: true, // ✅ 업로드 불가 플래그 추가
  },
};

export const GridFileUploadF = (
  extension = '', // 확장자 그룹 키: 'pdf', 'img' 등
  gridRef,
  props,
  options = {}
) => {
  const input = document.createElement('input');
  input.type = 'file';
  input.multiple = true;

  const { exts } = extensionMap[extension] || {};
  input.accept = exts?.map((ext) => `.${ext}`).join(',') || '';

  // 배열로 받아 허용되는 파일 확장자 여러 개 허용할 수 있도록 변경 주석처리한 코드는 1개의 확장자만 받음
  // const keys = Array.isArray(extension) ? extension : [extension];
  // const exts = keys.flatMap((key) => extensionMap[key]?.exts || []);
  // input.accept = exts.map((ext) => `.${ext}`).join(',');
  input.style.display = 'none';

  input.onchange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const rowsToAdd = [];
    const invalidCharRegex = /[^\w가-힣\s().\[\]_,&-]/g; // 영문/숫자/한글/공백/( ) . _ -[ ] , &

    for (const file of files) {
      const ext = file.name.split('.').pop()?.toLowerCase();

      //엑셀 업로드 차단 옵션 적용
      if (['xls', 'xlsx'].includes(ext) && options.blockExcel) {
        props.dispatch?.(
          showSnackbar({
            message: '엑셀 파일은 업로드할 수 없습니다.',
            type: 'warning',
          })
        );
        continue;
      }

      //PPT 업로드 차단 옵션 적용
      if (['ppt', 'pptx'].includes(ext) && options.blockPpt) {
        props.dispatch?.(
          showSnackbar({
            message: 'PPT 파일은 업로드할 수 없습니다.',
            type: 'warning',
          })
        );
        continue;
      }

      //워드 업로드 차단 옵션 적용
      if (['doc', 'docx'].includes(ext) && options.blockDocx) {
        props.dispatch?.(
          showSnackbar({
            message: '워드 파일은 업로드할 수 없습니다.',
            type: 'warning',
          })
        );
        continue;
      }

      //이미지 업로드 차단 옵션 적용
      if (
        [
          'jpeg',
          'jpg',
          'png',
          'gif',
          'svg',
          'ico',
          '.webp',
          '.bmp',
          '.psd',
          '.ai',
          '.indd',
        ].includes(ext) &&
        options.blockImage
      ) {
        props.dispatch?.(
          showSnackbar({
            message: '이미지 파일은 업로드할 수 없습니다.',
            type: 'warning',
          })
        );
        continue;
      }

      //동영상 업로드 차단 옵션 적용
      if (['mp4', 'avi', 'mov', 'wmv'].includes(ext) && options.blockVideo) {
        props.dispatch?.(
          showSnackbar({
            message: '동영상 파일은 업로드할 수 없습니다.',
            type: 'warning',
          })
        );
        continue;
      }

      if (exts && !exts.includes(ext)) {
        props.dispatch(
          showSnackbar({
            message:
              extensionMap[extension]?.message ||
              '허용되지 않는 파일 형식입니다.',
            type: 'warning',
          })
        );
        continue;
      }
      if (invalidCharRegex.test(file.name)) {
        props.dispatch?.(
          showSnackbar({
            message: `파일명에 특수문자가 포함되어 있습니다: ${file.name}`,
            type: 'warning',
          })
        );
        continue;
      }
      // if (file.size > maxFileSize) {
      //   props.dispatch?.(
      //     showSnackbar({
      //       message: `파일 용량이 50MB를 초과합니다: ${file.name}`,
      //       type: 'warning',
      //     })
      //   );
      //   continue;
      // }

      const buffer = await file.arrayBuffer(); // async로 Blob 생성
      const mimeType = file.type || 'application/octet-stream';
      const blob = new Blob([buffer], { type: mimeType });
      const timestamp = dayjs().format('YYYYMMDDHHmmss');
      const saveName = `${timestamp}_${file.name}`;

      const fileToSave = new File([blob], saveName, { type: mimeType });

      // 미리보기 파일 저장
      const previewPath = (await props.SaveLogicPreview?.(fileToSave)) || '';
      const saveNameField = options.saveNameField || 'saveName';

      const rowData = {
        mapBlob: blob,
        mapFile: fileToSave,
        mapFileName: file.name,
        fileName: file.name,
        [saveNameField]: saveName,
        previewFilePath: previewPath,
        date: dayjs().format('YYYY-MM-DD'),
        no: uuidv4(),
        newYn: 'Y',
        fileYn: 'Y',
        // 필요한 다른 필드 추가
      };

      rowsToAdd.push(rowData);
    }

    // AG Grid에 행 추가
    if (gridRef.current && rowsToAdd.length > 0) {
      const api = gridRef.current.api;
      // api.applyTransaction({ add: rowsToAdd });
      const res = api.applyTransaction({ add: rowsToAdd });

      // 상태 저장 (수정된 데이터 누적 저장)
      props.setChangedRowData1?.((prev) => {
        return [...prev, ...rowsToAdd];
      });
      // 상태 저장 (수정된 데이터 누적 저장)
      props.setChangedRowData?.((prev) => [...prev, ...rowsToAdd]);
      // if (props.useCustomFormat) {
      //   props.setChangedRowData2?.((prev) => [...prev, ...rowsToAdd]);
      // } else {
      //   props.setChangedRowData1?.((prev) => [...prev, ...rowsToAdd]);
      // }

      // 마지막에 추가된 행 SELECT
      setTimeout(() => {
        if (res?.add?.length > 0) {
          const lastRowData = res.add[res.add.length - 1];
          const rowNode = api.getRowNode(lastRowData.id); // ID 기준

          if (rowNode) {
            api.deselectAll(); // CheckBox 전체 해제
            rowNode.setSelected(true);
            api.ensureIndexVisible(rowNode.rowIndex, 'middle');
          }
        }
      }, 0);
    }
  };

  document.body.appendChild(input);
  input.click();
  setTimeout(() => {
    document.body.removeChild(input);
  }, 1000);
};
